import {
  DatabaseStatus,
  DatabaseUpdateSource,
  IDatabaseInstanceDocument,
  IPracticeMigration,
  PENDING_DATABASE_STATUSES,
} from '@principle-theorem/practice-migrations/interfaces';
import { RootCollection } from '@principle-theorem/principle-core/interfaces';
import {
  asColRef,
  CollectionReference,
  DocumentReference,
  Firestore,
  firstResult$,
  Timestamp,
  toTimestamp,
  type WithRef,
  where,
  writeBatch,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { getDatabaseInstanceName } from './source/source-helpers';

export interface IRegisterInstanceRequest {
  instanceName: string;
  region: string;
  isDefault: boolean;
  status: DatabaseStatus;
}

export class MigrationDatabase {
  static instancesColRef(): CollectionReference<IDatabaseInstanceDocument> {
    return asColRef<IDatabaseInstanceDocument>(
      RootCollection.MigrationDatabaseInstances
    );
  }

  static async instanceDoc(
    instanceName: string
  ): Promise<WithRef<IDatabaseInstanceDocument> | undefined> {
    return Firestore.firstResult(
      MigrationDatabase.instancesColRef(),
      where('instanceName', '==', instanceName)
    );
  }

  static allInstances$(): Observable<WithRef<IDatabaseInstanceDocument>[]> {
    return Firestore.query$<IDatabaseInstanceDocument>(
      MigrationDatabase.instancesColRef()
    );
  }

  static instanceDoc$(
    instanceName: string
  ): Observable<WithRef<IDatabaseInstanceDocument> | undefined> {
    return firstResult$<IDatabaseInstanceDocument>(
      MigrationDatabase.instancesColRef(),
      where('instanceName', '==', instanceName)
    );
  }

  static async allInstances(): Promise<WithRef<IDatabaseInstanceDocument>[]> {
    return Firestore.getDocs(MigrationDatabase.instancesColRef());
  }

  static async runningInstances(): Promise<
    WithRef<IDatabaseInstanceDocument>[]
  > {
    return Firestore.getDocs(
      MigrationDatabase.instancesColRef(),
      where('status', '==', DatabaseStatus.Running)
    );
  }

  static async defaultInstance(): Promise<
    WithRef<IDatabaseInstanceDocument> | undefined
  > {
    const results = await Firestore.getDocs(
      MigrationDatabase.instancesColRef(),
      where('isDefault', '==', true)
    );
    return results[0];
  }

  static defaultInstance$(): Observable<
    WithRef<IDatabaseInstanceDocument> | undefined
  > {
    return firstResult$<IDatabaseInstanceDocument>(
      MigrationDatabase.instancesColRef(),
      where('isDefault', '==', true)
    );
  }

  static async addInstance(
    request: IRegisterInstanceRequest,
    updatedBy: DatabaseUpdateSource
  ): Promise<DocumentReference<IDatabaseInstanceDocument>> {
    const existing = await MigrationDatabase.instanceDoc(request.instanceName);
    if (existing) {
      return existing.ref;
    }

    const ref = await Firestore.addDoc(MigrationDatabase.instancesColRef(), {
      ...request,
      lastUpdated: toTimestamp(),
      lastUpdatedBy: updatedBy,
    });

    if (request.isDefault) {
      await MigrationDatabase.setDefaultInstance(request.instanceName);
    }

    return ref;
  }

  static async setDefaultInstance(instanceName: string): Promise<void> {
    const instances = await MigrationDatabase.allInstances();
    const target = instances.find(
      (instance) => instance.instanceName === instanceName
    );
    if (!target) {
      throw new Error(
        `Database instance not found in registry: ${instanceName}`
      );
    }

    const batch = writeBatch();
    instances.forEach((instance) =>
      batch.update(instance.ref, {
        isDefault: instance.ref.id === target.ref.id,
      })
    );
    await batch.commit();
  }

  static async resolveInstance(
    migration: IPracticeMigration
  ): Promise<WithRef<IDatabaseInstanceDocument> | undefined> {
    if (migration.instanceRef) {
      return Firestore.safeGetDoc(migration.instanceRef);
    }

    const configuredName = getDatabaseInstanceName(migration.source);
    if (configuredName) {
      return MigrationDatabase.instanceDoc(configuredName);
    }

    return MigrationDatabase.defaultInstance();
  }

  static resolveInstance$(
    migration: IPracticeMigration
  ): Observable<WithRef<IDatabaseInstanceDocument> | undefined> {
    if (migration.instanceRef) {
      return Firestore.safeDoc$(migration.instanceRef);
    }

    const configuredName = getDatabaseInstanceName(migration.source);
    if (configuredName) {
      return MigrationDatabase.instanceDoc$(configuredName);
    }

    return MigrationDatabase.defaultInstance$();
  }

  static async updateInstanceDoc(
    instanceName: string,
    status: DatabaseStatus,
    updatedBy: DatabaseUpdateSource
  ): Promise<void> {
    const now = toTimestamp();
    const instance = await MigrationDatabase.instanceDoc(instanceName);
    if (!instance) {
      return;
    }

    const pendingSince = MigrationDatabase._resolvePendingSince(
      status,
      instance
    );

    const update = {
      ...instance,
      status,
      lastUpdated: now,
      lastUpdatedBy: updatedBy,
      ...(pendingSince ? { pendingSince } : {}),
    };

    await Firestore.saveDoc(update);
  }

  private static _resolvePendingSince(
    status: DatabaseStatus,
    instance: WithRef<IDatabaseInstanceDocument>
  ): Timestamp | undefined {
    if (!PENDING_DATABASE_STATUSES.includes(status)) {
      return;
    }
    if (
      PENDING_DATABASE_STATUSES.includes(instance.status) &&
      instance.pendingSince
    ) {
      return instance.pendingSince;
    }
    return toTimestamp();
  }
}
